(() => {
  const state = {
    lastTrigger: null,
    lightboxTrigger: null,
    modalOpen: false,
    motionFrame: null,
    activeLightbox: null,
  };

  const selectors = {
    filters: '[data-concepts-filters]',
    filterChipStrip: '[data-concepts-filter-chip-strip]',
    cards: '[data-concept-card]',
    filterButton: '.concepts-filter-chip',
    filtersToggle: '[data-concepts-filters-toggle]',
    clearButton: '[data-concepts-clear]',
    modal: '[data-concepts-modal]',
    modalDialog: '[data-concepts-modal-dialog]',
    modalBody: '[data-concepts-modal-body]',
    modalClose: '[data-concepts-modal-close]',
    cardOpen: '[data-concept-open]',
    template: '[data-concept-modal-template]',
    galleryGridOpen: '[data-gallery-grid-open]',
    lightbox: '[data-concepts-lightbox]',
    lightboxSlide: '[data-lightbox-slide]',
    lightboxClose: '[data-concepts-lightbox-close]',
    lightboxNav: '[data-lightbox-dir]',
    copyToggle: '[data-concepts-copy-toggle]',
    copyBody: '[data-concepts-copy-body]',
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const normalizeToken = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim();

  const normalizeList = (value) =>
    String(value || '')
      .split(',')
      .map((item) => normalizeToken(item))
      .filter(Boolean);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getContext = () => ({
    filters: document.querySelector(selectors.filters),
    cards: Array.from(document.querySelectorAll(selectors.cards)),
    modal: document.querySelector(selectors.modal),
  });

  const getFocusableElements = (container) =>
    Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      if (element.closest('[hidden]')) return false;
      return !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true';
    });

  const getActiveFilters = (filtersRoot) => {
    const active = {};
    if (!filtersRoot) return active;

    filtersRoot.querySelectorAll(`${selectors.filterButton}[aria-pressed="true"]`).forEach((button) => {
      const group = button.getAttribute('data-filter-group');
      const value = normalizeToken(button.getAttribute('data-filter-value'));
      if (!group || !value) return;
      if (!active[group]) active[group] = new Set();
      active[group].add(value);
    });

    return active;
  };

  const getCardValues = (card, group) => {
    const value = card.getAttribute(`data-${group}`) || '';
    return normalizeList(value);
  };

  const cardMatchesFilters = (card, activeFilters) => {
    const groups = Object.keys(activeFilters);
    if (!groups.length) return true;

    return groups.every((group) => {
      const values = activeFilters[group];
      if (!values || !values.size) return true;

      const cardValues = getCardValues(card, group);
      if (!cardValues.length) return false;

      return Array.from(values).some((value) => cardValues.includes(value));
    });
  };

  const updateCount = (filtersRoot, visibleCount, totalCount) => {
    if (!filtersRoot) return;
    const visible = filtersRoot.querySelector('[data-concepts-visible-count]');
    const total = filtersRoot.querySelector('[data-concepts-total-count]');
    const clearButton = filtersRoot.querySelector(selectors.clearButton);
    const hasActiveFilters = filtersRoot.querySelector(`${selectors.filterButton}[aria-pressed="true"]`);

    if (visible) visible.textContent = String(visibleCount);
    if (total) total.textContent = String(totalCount);
    if (clearButton) clearButton.disabled = !hasActiveFilters;
  };

  const measureChipLimit = (chipStrip, chips, toggle) => {
    if (!chipStrip || !chips.length) return chips.length;

    const bar = chipStrip.closest('.concepts-filters__bar');
    if (!bar) return chips.length;

    const barStyles = window.getComputedStyle(bar);
    const barGap = parseFloat(barStyles.columnGap || barStyles.gap || '0') || 0;
    const countEl = bar.querySelector('[data-concepts-count]');
    const clearEl = bar.querySelector('[data-concepts-clear]');
    const barWidth = bar.clientWidth;
    const countWidth = countEl ? Math.ceil(countEl.getBoundingClientRect().width) : 0;
    const clearWidth = clearEl ? Math.ceil(clearEl.getBoundingClientRect().width) : 0;
    const available = barWidth - countWidth - clearWidth - barGap * 2;

    if (available <= 0) return chips.length;

    const savedHidden = chips.map((c) => c.hidden);
    const savedToggleHidden = toggle.hidden;
    const savedToggleText = toggle.textContent;

    chips.forEach((c) => { c.hidden = false; });
    toggle.hidden = false;
    toggle.textContent = 'More';

    const chipStyles = window.getComputedStyle(chipStrip);
    const chipGap = parseFloat(chipStyles.gap || chipStyles.columnGap || '0') || 0;
    const toggleWidth = Math.ceil(toggle.getBoundingClientRect().width);
    const widths = chips.map((c) => Math.ceil(c.getBoundingClientRect().width));

    chips.forEach((c, i) => { c.hidden = savedHidden[i]; });
    toggle.hidden = savedToggleHidden;
    toggle.textContent = savedToggleText;

    let total = 0;
    for (let i = 0; i < widths.length; i++) {
      const added = i > 0 ? chipGap + widths[i] : widths[i];

      if (i === widths.length - 1) {
        if (total + added <= available + 1) return widths.length;
        return Math.max(i, 1);
      }

      if (total + added + chipGap + toggleWidth > available + 1) {
        return Math.max(i, 1);
      }

      total += added;
    }

    return widths.length;
  };

  let cachedChipLimit = null;

  const syncFilterChipVisibility = () => {
    const { filters } = getContext();
    if (!filters) return;

    const chipStrip = filters.querySelector(selectors.filterChipStrip);
    const toggle = filters.querySelector(selectors.filtersToggle);
    const chips = Array.from(filters.querySelectorAll(selectors.filterButton));
    if (!chipStrip || !toggle || !chips.length) return;

    const expanded = filters.dataset.filtersExpanded === 'true';

    if (expanded) {
      chips.forEach((c) => { c.hidden = false; });
      const needsToggle = cachedChipLimit !== null && cachedChipLimit < chips.length;
      toggle.hidden = !needsToggle;
      toggle.textContent = 'Less';
      toggle.setAttribute('aria-expanded', 'true');
    } else {
      const isMobile = window.innerWidth < 750;
      const limit = isMobile ? Math.min(7, chips.length) : measureChipLimit(chipStrip, chips, toggle);
      cachedChipLimit = limit;
      const needsToggle = limit < chips.length;
      chips.forEach((c, i) => { c.hidden = needsToggle && i >= limit; });
      toggle.hidden = !needsToggle;
      toggle.textContent = 'More';
      toggle.setAttribute('aria-expanded', 'false');
    }
  };


  const resetCardMotion = (card) => {
    card.style.setProperty('--concept-rotate-x', '0deg');
    card.style.setProperty('--concept-rotate-y', '0deg');
    card.style.setProperty('--concept-shift', '0px');
    card.style.setProperty('--concept-scale', '1');
    card.style.setProperty('--concept-opacity', '1');
  };

  const updateCardMotion = () => {
    state.motionFrame = null;

    const { cards } = getContext();
    if (!cards.length) return;

    if (motionQuery.matches) {
      cards.forEach(resetCardMotion);
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportCenter = viewportHeight * 0.56;
    const isMobile = viewportWidth < 750;

    cards.forEach((card) => {
      if (card.hidden || card.classList.contains('is-hidden')) {
        resetCardMotion(card);
        return;
      }

      const rect = card.getBoundingClientRect();
      const cardCenter = rect.top + rect.height / 2;
      const distance = clamp((cardCenter - viewportCenter) / viewportHeight, -1.2, 1.2);
      const reveal = clamp(1 - Math.abs(distance) * 1.08, 0, 1);
      const easedReveal = easeOutCubic(reveal);
      const shiftRange = isMobile ? 18 : 30;
      const scale = 0.94 + easedReveal * 0.06;
      const opacity = 0.5 + easedReveal * 0.5;

      card.style.setProperty('--concept-rotate-x', '0deg');
      card.style.setProperty('--concept-rotate-y', '0deg');
      card.style.setProperty('--concept-shift', `${(distance * shiftRange).toFixed(2)}px`);
      card.style.setProperty('--concept-scale', scale.toFixed(3));
      card.style.setProperty('--concept-opacity', opacity.toFixed(3));
    });
  };

  const scheduleMotionUpdate = () => {
    if (state.motionFrame) return;
    state.motionFrame = window.requestAnimationFrame(updateCardMotion);
  };

  const applyFilters = () => {
    const { filters, cards } = getContext();
    const activeFilters = getActiveFilters(filters);

    let visibleCount = 0;
    cards.forEach((card) => {
      const isVisible = cardMatchesFilters(card, activeFilters);
      card.classList.toggle('is-hidden', !isVisible);
      card.hidden = !isVisible;
      if (isVisible) visibleCount += 1;
    });

    updateCount(filters, visibleCount, cards.length);
    syncFilterChipVisibility();
    scheduleMotionUpdate();
  };

  const clearFilters = () => {
    const { filters } = getContext();
    if (!filters) return;
    filters.dataset.filtersExpanded = 'false';
    filters.querySelectorAll(selectors.filterButton).forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
    });
    applyFilters();
  };

  const getModalElements = () => {
    const { modal } = getContext();
    if (!modal) return {};

    return {
      modal,
      dialog: modal.querySelector(selectors.modalDialog),
      body: modal.querySelector(selectors.modalBody),
    };
  };

  const getLightboxState = (lightbox) => {
    if (!lightbox) return null;

    return {
      slides: Array.from(lightbox.querySelectorAll(selectors.lightboxSlide)),
      close: lightbox.querySelector(selectors.lightboxClose),
      prev: lightbox.querySelector(`${selectors.lightboxNav}[data-lightbox-dir="prev"]`),
      next: lightbox.querySelector(`${selectors.lightboxNav}[data-lightbox-dir="next"]`),
    };
  };

  const setLightboxIndex = (lightbox, rawIndex) => {
    const lightboxState = getLightboxState(lightbox);
    if (!lightboxState || !lightboxState.slides.length) return;

    const index = clamp(rawIndex, 0, lightboxState.slides.length - 1);
    lightbox.dataset.activeIndex = String(index);

    lightboxState.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === index;
      slide.hidden = !isActive;
      slide.classList.toggle('is-active', isActive);
    });

    if (lightboxState.prev) lightboxState.prev.disabled = index === 0;
    if (lightboxState.next) lightboxState.next.disabled = index === lightboxState.slides.length - 1;
  };

  const closeLightbox = (lightbox, options = {}) => {
    if (!lightbox) return;

    const restoreFocus = options.restoreFocus !== false;
    lightbox.hidden = true;
    lightbox.setAttribute('aria-hidden', 'true');

    if (state.activeLightbox === lightbox) {
      state.activeLightbox = null;
    }

    if (restoreFocus && state.lightboxTrigger && document.contains(state.lightboxTrigger)) {
      state.lightboxTrigger.focus();
    }

    state.lightboxTrigger = null;
  };

  const openLightbox = (lightbox, rawIndex, trigger) => {
    if (!lightbox) return;

    const lightboxState = getLightboxState(lightbox);
    if (!lightboxState || !lightboxState.slides.length) return;

    const index = clamp(rawIndex, 0, lightboxState.slides.length - 1);
    state.lightboxTrigger = trigger || document.activeElement;
    state.activeLightbox = lightbox;
    lightbox.hidden = false;
    lightbox.setAttribute('aria-hidden', 'false');
    setLightboxIndex(lightbox, index);

    const focusTarget = lightboxState.close || lightboxState.prev || lightboxState.next;
    window.requestAnimationFrame(() => {
      if (focusTarget) focusTarget.focus();
    });
  };

  const syncCopyToggle = (toggle, body) => {
    if (!toggle || !body) return;
    const expanded = body.classList.contains('is-expanded');
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.textContent = expanded ? 'Show less' : 'Read more';
  };

  const initCopyToggle = (toggle) => {
    if (!toggle || toggle.dataset.conceptsCopyInit === 'true') return;
    const wrap = toggle.closest('.concepts-modal__statement-wrap');
    const body = wrap ? wrap.querySelector(selectors.copyBody) : null;
    if (!body) return;

    toggle.dataset.conceptsCopyInit = 'true';
    syncCopyToggle(toggle, body);
  };

  const populateModal = (card) => {
    const { modal, body } = getModalElements();
    if (!body) return false;

    const template = card.querySelector(selectors.template);
    if (!template) return false;

    body.innerHTML = '';
    const lightboxHost = document.querySelector('[data-concepts-lightbox-host]');
    if (lightboxHost) lightboxHost.innerHTML = '';

    body.appendChild(template.content.cloneNode(true));

    const lightbox = body.querySelector(selectors.lightbox);
    if (lightbox && lightboxHost) {
      lightboxHost.appendChild(lightbox);
    }

    body.querySelectorAll(selectors.copyToggle).forEach(initCopyToggle);
    return true;
  };

  const openModal = (card, trigger) => {
    const { modal, dialog } = getModalElements();
    if (!modal || !dialog || !populateModal(card)) return;

    state.lastTrigger = trigger;
    state.modalOpen = true;
    state.activeLightbox = null;
    state.lightboxTrigger = null;

    modal.hidden = false;
    document.body.classList.add('concepts-modal-open');

    const focusable = getFocusableElements(dialog);
    const target = focusable[0] || dialog;
    window.requestAnimationFrame(() => {
      target.focus();
    });
  };

  const closeModal = () => {
    const { modal, body } = getModalElements();
    if (!modal) return;

    if (state.activeLightbox) {
      closeLightbox(state.activeLightbox, { restoreFocus: false });
    }

    modal.hidden = true;
    state.modalOpen = false;
    if (body) body.innerHTML = '';
    const lightboxHost = document.querySelector('[data-concepts-lightbox-host]');
    if (lightboxHost) lightboxHost.innerHTML = '';
    document.body.classList.remove('concepts-modal-open');

    if (state.lastTrigger && document.contains(state.lastTrigger)) {
      state.lastTrigger.focus();
    }

    state.lastTrigger = null;
  };

  const trapFocus = (event) => {
    if (!state.modalOpen || event.key !== 'Tab') return;

    const { dialog } = getModalElements();
    const container =
      state.activeLightbox && !state.activeLightbox.hidden ? state.activeLightbox : dialog;
    if (!container) return;

    const focusable = getFocusableElements(container);
    if (!focusable.length) {
      event.preventDefault();
      container.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleGalleryGridOpen = (button) => {
    const lightboxHost = document.querySelector('[data-concepts-lightbox-host]');
    const lightbox = lightboxHost ? lightboxHost.querySelector(selectors.lightbox) : null;
    if (!lightbox) return;

    const index = Number(button.getAttribute('data-gallery-grid-open'));
    if (!Number.isFinite(index)) return;

    openLightbox(lightbox, index, button);
  };

  const handleLightboxNav = (button) => {
    const lightbox = button.closest(selectors.lightbox);
    if (!lightbox) return;

    const currentIndex = Number(lightbox.dataset.activeIndex || 0);
    const direction = button.getAttribute('data-lightbox-dir') === 'prev' ? -1 : 1;
    setLightboxIndex(lightbox, currentIndex + direction);
  };

  const handleCopyToggle = (button) => {
    const wrap = button.closest('.concepts-modal__statement-wrap');
    const body = wrap ? wrap.querySelector(selectors.copyBody) : null;
    if (!body) return;

    const isExpanded = body.classList.contains('is-expanded');
    body.classList.toggle('is-expanded', !isExpanded);
    body.classList.toggle('is-collapsed', isExpanded);
    syncCopyToggle(button, body);
  };

  const handleClick = (event) => {
    const filtersToggle = event.target.closest(selectors.filtersToggle);
    if (filtersToggle) {
      const { filters } = getContext();
      if (!filters) return;
      const expanded = filters.dataset.filtersExpanded === 'true';
      filters.dataset.filtersExpanded = expanded ? 'false' : 'true';
      syncFilterChipVisibility();
      return;
    }

    const clearButton = event.target.closest(selectors.clearButton);
    if (clearButton) {
      clearFilters();
      return;
    }

    const filterButton = event.target.closest(selectors.filterButton);
    if (filterButton) {
      const isPressed = filterButton.getAttribute('aria-pressed') === 'true';
      filterButton.setAttribute('aria-pressed', isPressed ? 'false' : 'true');
      applyFilters();
      return;
    }

    const openTrigger = event.target.closest(selectors.cardOpen);
    if (openTrigger) {
      const card = openTrigger.closest(selectors.cards);
      if (card) openModal(card, openTrigger);
      return;
    }

    const modalClose = event.target.closest(selectors.modalClose);
    if (modalClose) {
      closeModal();
      return;
    }

    const galleryGridOpen = event.target.closest(selectors.galleryGridOpen);
    if (galleryGridOpen) {
      handleGalleryGridOpen(galleryGridOpen);
      return;
    }

    const lightboxClose = event.target.closest(selectors.lightboxClose);
    if (lightboxClose) {
      const lightbox = lightboxClose.closest(selectors.lightbox);
      closeLightbox(lightbox);
      return;
    }

    const lightboxNav = event.target.closest(selectors.lightboxNav);
    if (lightboxNav) {
      handleLightboxNav(lightboxNav);
      return;
    }

    const copyToggle = event.target.closest(selectors.copyToggle);
    if (copyToggle) {
      handleCopyToggle(copyToggle);
    }
  };

  const handleKeydown = (event) => {
    if (event.key === 'Escape' && state.modalOpen) {
      event.preventDefault();
      if (state.activeLightbox && !state.activeLightbox.hidden) {
        closeLightbox(state.activeLightbox);
        return;
      }
      closeModal();
      return;
    }

    trapFocus(event);
  };

  const syncPage = () => {
    applyFilters();
    syncFilterChipVisibility();
    scheduleMotionUpdate();
  };

  if (document.documentElement.dataset.conceptsPageInit === 'true') {
    syncPage();
    return;
  }

  document.documentElement.dataset.conceptsPageInit = 'true';
  document.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('DOMContentLoaded', syncPage);
  document.addEventListener('shopify:section:load', syncPage);
  window.addEventListener('scroll', scheduleMotionUpdate, { passive: true });
  window.addEventListener('resize', () => {
    cachedChipLimit = null;
    syncFilterChipVisibility();
    scheduleMotionUpdate();
  });

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', scheduleMotionUpdate);
  } else if (typeof motionQuery.addListener === 'function') {
    motionQuery.addListener(scheduleMotionUpdate);
  }

  syncPage();
})();
